/**
 * 阅读行为事件类型配置
 *
 * 用途：
 * 1. 事件类型中英文映射
 * 2. 事件类型颜色标记
 * 3. 事件类型图标配置
 *
 * 遵循配置化原则，避免硬编码
 */

export interface EventTypeConfig {
  /** 事件类型英文标识 */
  type: string
  /** 中文显示名称 */
  label: string
  /** Ant Design 标签颜色 */
  color: string
  /** 事件描述 */
  description: string
}

/**
 * 事件类型映射表
 */
export const EVENT_TYPE_CONFIG: Record<string, EventTypeConfig> = {
  'CARD_OPENED': {
    type: 'CARD_OPENED',
    label: '打开单词卡片',
    color: 'blue',
    description: '用户点击单词查看详情',
  },
  'EXPAND_LEARNING': {
    type: 'EXPAND_LEARNING',
    label: '扩展学习',
    color: 'purple',
    description: '用户展开单词卡片学习更多内容',
  },
  'EXAMPLES_VIEWED': {
    type: 'EXAMPLES_VIEWED',
    label: '查看例句',
    color: 'cyan',
    description: '用户查看单词例句',
  },
  'AUDIO_PLAYED': {
    type: 'AUDIO_PLAYED',
    label: '播放音频',
    color: 'green',
    description: '用户播放单词发音',
  },
  'USER_CLASSIFIED_KNOWN': {
    type: 'USER_CLASSIFIED_KNOWN',
    label: '标记为已知',
    color: 'success',
    description: '用户主动标记该词为已知',
  },
  'USER_CLASSIFIED_UNKNOWN': {
    type: 'USER_CLASSIFIED_UNKNOWN',
    label: '标记为生词',
    color: 'error',
    description: '用户主动标记该词为生词',
  },
  'LISTENED_PARAGRAPH': {
    type: 'LISTENED_PARAGRAPH',
    label: '段落听读',
    color: 'default',
    description: '听读段落中的被动接触',
  },
  'EPISODE_COMPLETION_BATCH': {
    type: 'EPISODE_COMPLETION_BATCH',
    label: '读完批量接触',
    color: 'default',
    description: '读完时全文不同词各记一份贡献',
  },
  'COLLOCATIONS_VIEWED': {
    type: 'COLLOCATIONS_VIEWED',
    label: '查看搭配',
    color: 'default',
    description: '用户查看词汇搭配',
  },
  'AI_PHONICS_PLAYED': {
    type: 'AI_PHONICS_PLAYED',
    label: 'AI 拼读',
    color: 'default',
    description: '用户播放 AI 拼读',
  },
  'READING_TOUCH_COMPLETION': {
    type: 'READING_TOUCH_COMPLETION',
    label: '被动阅读',
    color: 'orange',
    description: '读完时记录未查词、未点不认识的词',
  }
}

/**
 * 获取事件类型配置
 */
export const getEventTypeConfig = (eventType: string): EventTypeConfig => {
  return EVENT_TYPE_CONFIG[eventType] || {
    type: eventType,
    label: eventType,
    color: 'default',
    description: '未知事件类型',
  }
}

/**
 * 获取所有事件类型
 */
export const getAllEventTypes = (): EventTypeConfig[] => {
  return Object.values(EVENT_TYPE_CONFIG)
}

/**
 * 事件类型到中文的映射（快捷访问）
 */
export const EVENT_TYPE_LABELS: Record<string, string> = Object.entries(EVENT_TYPE_CONFIG).reduce(
  (acc, [key, config]) => {
    acc[key] = config.label
    return acc
  },
  {} as Record<string, string>
)

/**
 * 事件类型到颜色的映射（快捷访问）
 */
export const EVENT_TYPE_COLORS: Record<string, string> = Object.entries(EVENT_TYPE_CONFIG).reduce(
  (acc, [key, config]) => {
    acc[key] = config.color
    return acc
  },
  {} as Record<string, string>
)
